// ═══════════════════════════════════════════════════════════════════════
// Paramètres — Centre de configuration (Lot 1). Une seule porte : chaque domaine est une tuile. Les
// domaines dont les règles passent déjà par le Centre (temps, congés, heures supplémentaires,
// réglementation) s'ouvrent ici ; les autres renvoient vers leurs écrans actuels (convergence prévue).
// Le moteur contient les capacités, Paramètres contient les règles, l'IAM contrôle qui les administre.
// ═══════════════════════════════════════════════════════════════════════
import { useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Settings, ArrowRight, ExternalLink } from 'lucide-react';
import Layout from '../../components/Layout';
import { EnTetePage, Carte, Badge, BandeauInfo, Squelette } from '../../design/composants';
import { useChargement } from '../temps/reglagesCommun';
import { lireCfg, NavParametres, SLUG_DOMAINE } from './configurationCommun';

function Compteurs({ d }) {
  const c = d.compteurs;
  if (!c) return null;
  if (d.par_mandat) {
    return <p style={{ margin: '6px 0', fontSize: 14 }}>{c.gabarits} gabarit(s) · {c.regles} règle(s) réglées par dispositif{c.reglements_valides !== undefined ? ` · ${c.reglements_valides} règlement(s) validé(s)` : ''}{c.a_valider ? ` · ${c.a_valider} à valider` : ''}</p>;
  }
  if (d.code === 'REGLEMENTATION') {
    return <p style={{ margin: '6px 0', fontSize: 14 }}>{c.valides} version(s) validée(s){c.a_valider ? ` · ${c.a_valider} à valider` : ''}{c.brouillons ? ` · ${c.brouillons} en préparation` : ''}</p>;
  }
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '8px 0' }}>
      <Badge ton="succes">{c.actives} / {c.regles} réglée(s)</Badge>
      {c.incompletes > 0 && <Badge ton="alerte">{c.incompletes} non réglée(s)</Badge>}
      {c.a_valider > 0 && <Badge ton="info">{c.a_valider} à valider</Badge>}
      {c.effet_non_prouve > 0 && <Badge ton="neutre" title="Règles lues par le calcul dont l'effet n'est pas encore couvert par un test">{c.effet_non_prouve} effet non prouvé</Badge>}
    </div>
  );
}

function Tuile({ d }) {
  const vers = d.code === 'REGLEMENTATION' ? '/parametres/reglementation' : SLUG_DOMAINE[d.code] ? `/parametres/${SLUG_DOMAINE[d.code]}` : null;
  return (
    <Carte titre={d.libelle}>
      <p style={{ margin: 0, fontSize: 14, color: 'var(--sg-texte-2)' }}>{d.description}</p>
      <Compteurs d={d} />
      {vers && (d.droits ? d.droits.lire : true) && (
        <Link to={vers} className="rh-btn rh-btn-primaire rh-btn-petit" style={{ marginTop: 6, textDecoration: 'none' }}>Ouvrir <ArrowRight size={14} aria-hidden="true" /></Link>
      )}
      {(d.ecrans || []).length > 0 && (
        <div style={{ marginTop: 8 }}>
          {!d.regles_au_centre && <p style={{ margin: '0 0 4px', fontSize: 12, color: 'var(--sg-texte-3, #666)' }}>Réglages actuels (rattachement au Centre prévu dans un prochain lot) :</p>}
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14 }}>
            {d.ecrans.map((e) => <li key={e.lien}><Link to={e.lien}>{e.libelle} <ExternalLink size={12} aria-hidden="true" /></Link></li>)}
          </ul>
        </div>
      )}
    </Carte>
  );
}

export default function CentreConfigurationPage() {
  const [params] = useSearchParams();
  const naviguer = useNavigate();
  // Anciens liens « /parametres?section=… » : écrans de référentiels existants.
  useEffect(() => { if (params.get('section')) naviguer(`/parametres/referentiels?section=${encodeURIComponent(params.get('section'))}`, { replace: true }); }, [params, naviguer]);
  const { donnees, erreur } = useChargement(() => lireCfg('/domaines'), []);
  const incompletes = (donnees?.domaines || []).reduce((a, d) => a + ((d.compteurs && d.compteurs.incompletes) || 0), 0);
  const aValider = (donnees?.domaines || []).reduce((a, d) => a + ((d.compteurs && d.compteurs.a_valider) || 0), 0);
  return (
    <Layout>
      <div className="rh-page">
        <EnTetePage fil={[{ libelle: 'Paramètres' }]} titre="Paramètres"
          sousTitre="Les règles de l'entreprise, datées et validées par une seconde personne. Le logiciel applique automatiquement la version en vigueur à chaque date." />
        <NavParametres actif="accueil" />
        {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
        {incompletes > 0 && (
          <BandeauInfo ton="alerte" titre="Configuration incomplète">
            {incompletes} règle(s) n'ont pas encore de valeur validée. Les calculs qui en dépendent ne produisent aucune conséquence (ni retenue, ni absence, ni heure supplémentaire payée) tant qu'elles ne sont pas réglées.
          </BandeauInfo>
        )}
        {aValider > 0 && <BandeauInfo ton="info" titre="À valider" action={<Link to="/parametres/a-valider">Voir</Link>}>{aValider} règle(s) attendent une validation.</BandeauInfo>}
        {!donnees && !erreur && <Squelette hauteur={120} />}
        {donnees && (
          <div className="rh-grille-cartes">
            {donnees.domaines.map((d) => <Tuile key={d.code} d={d} />)}
          </div>
        )}
        <p style={{ marginTop: 16, fontSize: 13, color: 'var(--sg-texte-2)', display: 'flex', gap: 6, alignItems: 'center' }}>
          <Settings size={14} aria-hidden="true" /> Qui peut préparer ou valider une règle se règle dans Habilitations (fonctions « Préparateur des paramètres » et « Valideur des paramètres »).
        </p>
      </div>
    </Layout>
  );
}
